import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Container,
    Alert,
} from '@mui/material';
import api, { formatApiErrorMessage } from '../services/api';
import ResendPasswordReset from '../components/auth/ResendPasswordReset';
import { authFormCardSx, authMarketingShellSx } from '../utils/authMarketingLayout';

const resetAuthShell = (body: React.ReactNode) => (
    <Box sx={(t) => authMarketingShellSx(t)}>
        <Container
            component="main"
            maxWidth="xs"
            sx={{
                position: 'relative',
                zIndex: 2,
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                py: 3,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {body}
            </Box>
        </Container>
    </Box>
);

const ResetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const navigate = useNavigate();

    function validatePassword(password: string): string | null {
        if (password.length < 8) return 'Password must be at least 8 characters long';
        if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
        if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
        if (!/[0-9]/.test(password)) return 'Password must contain at least one digit';
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return 'Password must contain at least one special character';
        return null;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const passwordError = validatePassword(password);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);

        try {
            await api.post('/auth/reset-password', { token, password });
            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            const status = err?.response?.status;
            const msg = formatApiErrorMessage(err?.response?.data, '');
            if (status === 429 || /too many attempts/i.test(msg)) {
                setError(msg || 'Too many attempts. Please try again later.');
            } else {
                setError(
                    typeof err?.response?.data === 'string'
                        ? err.response.data
                        : msg || 'Failed to reset password. The link may have expired.',
                );
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPassword(value);
        const error = validatePassword(value);
        setPasswordError(error || '');
    };

    if (!token) {
        return resetAuthShell(
            <Paper sx={(t) => authFormCardSx(t)}>
                <Alert severity="error">
                    Invalid or missing reset token. Please request a new password reset link.
                </Alert>
            </Paper>,
        );
    }

    return resetAuthShell(
        <Paper sx={(t) => authFormCardSx(t)}>
                    <Typography component="h1" variant="h5" align="center" gutterBottom>
                        Reset Your Password
                    </Typography>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Password has been reset successfully. Redirecting to login...
                        </Alert>
                    )}
                    {error === 'Invalid or expired reset token.' && (
                        <ResendPasswordReset />
                    )}
                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="New Password"
                            type="password"
                            id="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={handlePasswordChange}
                            error={!!passwordError}
                            helperText={passwordError || ''}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirmPassword"
                            label="Confirm New Password"
                            type="password"
                            id="confirmPassword"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Resetting...' : 'Reset Password'}
                        </Button>
                    </Box>
                </Paper>,
    );
};

export default ResetPasswordPage; 